/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  ComposedModal,
  FileUploaderDropContainer,
  FileUploaderItem,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'carbon-components-react';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import type { Noop } from '../../../../ts';
import { PreviewFile } from '../../../../ts/preview';
import { messages } from './messages';

// FIXME: carbon typing is wrong so we need to add
// the selectorPrimaryFocus property to modal
const ComposeModalWithFocus = ComposedModal as any;

export const MIMETYPE_JPEG = 'image/jpeg';
export const MIMETYPE_PNG = 'image/png';
export const ACCEPT_ARRAY = ['.jpeg', '.jpg', '.png'];

export interface ImageUploadModalProps {
  isOpen: boolean;
  isBackgroundSet: boolean;
  isAlternateRemoveText: boolean;
  onClose: Noop;
  onClear: Noop;
  onSubmit: (imageFile: File) => void;
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  isBackgroundSet,
  isAlternateRemoveText,
  onClose,
  onClear,
  onSubmit
}) => {
  const intl = useIntl();
  const [targetFile, setTargetFile] = useState<File | null>(null);

  const handleSubmit = () => {
    onSubmit(targetFile);
    setTargetFile(null);
  };

  const handleClose = () => {
    setTargetFile(null);
    onClose();
  };

  const handleFileAdd = (evt, { addedFiles = [] }) => {
    setTargetFile(addedFiles[0]);
  };

  const handleFileRemove = evt => {
    evt.preventDefault();
    setTargetFile(null);
  };
  const cancelButtonLabel = intl.formatMessage(messages.changeBackgroundCancel);
  const removeButtonLabel = isAlternateRemoveText
    ? intl.formatMessage(messages.removeBackgroundAlternateCpd)
    : intl.formatMessage(messages.removeBackground);

  const secondaryButtonsConfig = [
    { buttonText: cancelButtonLabel, onClick: handleClose },
    { buttonText: removeButtonLabel, onClick: onClear }
  ];
  const isClearButtonShown = isCpd && isBackgroundSet;

  const sizeError = targetFile?.size > PreviewFile.UPLOAD_MAX_SIZE;
  const formatError =
    !!targetFile &&
    targetFile.type !== MIMETYPE_JPEG &&
    targetFile.type !== MIMETYPE_PNG;
  const errorMsg = formatError
    ? intl.formatMessage(messages.uploadFormatError)
    : intl.formatMessage(messages.uploadSizeError);

  return (
    <ComposeModalWithFocus
      data-testid="image-upload-modal"
      open={isOpen}
      className="ImageUploadModal"
      selectorPrimaryFocus=".bx--file-browse-btn"
      onClose={handleClose}>
      <ModalHeader
        title={intl.formatMessage(messages.changeBackgroundWebsite)}
      />
      <ModalBody>
        <div className="ImageUploadModal__instruction">
          {isCpd ? (
            <FormattedMessage {...messages.uploadInstructionCpd} />
          ) : (
            <FormattedMessage {...messages.uploadInstruction} />
          )}
        </div>
        <div className="ImageUploadModal__limits">
          <FormattedMessage {...messages.uploadLimits} />
        </div>
        <div className="ImageUploadModal__import">
          <FileUploaderDropContainer
            accept={ACCEPT_ARRAY}
            labelText={intl.formatMessage(messages.dragOrClick)}
            onAddFiles={handleFileAdd}
          />
          {!!targetFile && (
            <div className="ImageUploadModal__itemContainer">
              <FileUploaderItem
                invalid={sizeError || formatError}
                errorSubject={errorMsg}
                name={targetFile?.name}
                status="edit"
                onDelete={handleFileRemove}
                iconDescription={intl.formatMessage(messages.removeFile)}
              />
            </div>
          )}
        </div>
      </ModalBody>
      {isClearButtonShown ? (
        <ModalFooter
          primaryButtonDisabled={!targetFile || sizeError || formatError}
          primaryButtonText={intl.formatMessage(messages.changeBackground)}
          secondaryButtons={secondaryButtonsConfig}
          onRequestClose={handleClose}
          onRequestSubmit={handleSubmit}
        />
      ) : (
        <ModalFooter
          primaryButtonDisabled={!targetFile || sizeError || formatError}
          primaryButtonText={intl.formatMessage(messages.changeBackground)}
          secondaryButtonText={intl.formatMessage(
            messages.changeBackgroundCancel
          )}
          onRequestClose={handleClose}
          onRequestSubmit={handleSubmit}
        />
      )}
    </ComposeModalWithFocus>
  );
};
